import {
  createFileRoute,
  notFound,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { z } from "zod";
import { isStoresOnly } from "@nexseed/shared";
import { useAuth } from "@/features/auth/store";
import { MasterPage } from "@/features/masters/components/master-page";
import { MASTERS, isMasterKey } from "@/features/masters/registry";
import type { MasterSpec } from "@/features/masters/spec";

const search = z.object({
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(200).default(50),
  q: z.string().optional(),
  sort: z.string().optional(),
  order: z.enum(["asc", "desc"]).default("asc"),
  active: z.enum(["true", "false", "all"]).default("true"),
  // the spec's one extra list filter (vendors: nature of business)
  filter: z.string().max(60).optional(),
});

/** One generic list page for every spec in the registry (/masters/facilities, /masters/crops, …). */
export const Route = createFileRoute("/_app/masters/$key")({
  params: {
    parse: ({ key }) => {
      if (!isMasterKey(key)) throw notFound();
      return { key };
    },
    stringify: (p) => p,
  },
  validateSearch: search,
  beforeLoad: ({ params }) => {
    if (!useAuth.getState().can(MASTERS[params.key].permission.view))
      throw redirect({ to: "/" });
  },
  component: Page,
});

function Page() {
  const { key } = Route.useParams();
  const search = Route.useSearch();
  // A Stores-only login's Vendors are the Stores vendors (user, 2026-10-17), whatever the URL says,
  // with no Nature of business picker to choose others (user, 2026-10-29).
  const storesOnly = useAuth((st) => isStoresOnly(st.can));
  const storesVendors = key === "vendors" && storesOnly;
  const s = storesVendors ? { ...search, filter: "stores" } : search;
  const navigate = useNavigate({ from: Route.fullPath });
  return (
    <MasterPage
      spec={storesVendors ? storesVendorsSpec(MASTERS.vendors) : MASTERS[key]}
      search={s}
      onSearch={(next) =>
        void navigate({ search: (prev) => ({ ...prev, ...next }) })
      }
    />
  );
}

/** The Vendor master as the store sees it: its own suppliers, the filter fixed on them. */
function storesVendorsSpec(spec: MasterSpec): MasterSpec {
  return {
    ...spec,
    description: "The store's suppliers: stickers, rolls, bags, chemicals.",
    // every row is a Stores vendor, with no nature of business
    columns: spec.columns.filter(
      (c) => c.id !== "storesVendor" && c.id !== "natureOfBusiness"
    ),
    listFilter: spec.listFilter && { ...spec.listFilter, locked: true },
  };
}
